import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './index.css';

const MultiSelectInput = ({
  options = [],
  selectedValues = [],
  onChange,
  maxSelection = 4,
  placeholder = "Select items...",
  searchPlaceholder = "Search options...",
  noOptionsMessage = "No options available",
  maxSelectionMessage = "Maximum items selected",
  enableSearch = true,
  disabled = false,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter options based on search term and already selected items
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()) &&
    !selectedValues.includes(option.value)
  );

  const handleSelect = (value) => {
    if (selectedValues.length >= maxSelection) return;
    
    const newSelected = [...selectedValues, value];
    onChange(newSelected);
    setSearchTerm('');
  };

  const handleRemove = (value) => {
    const newSelected = selectedValues.filter(v => v !== value);
    onChange(newSelected);
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', index.toString());
    setDraggedIndex(index);
    e.target.style.opacity = '0.5';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    const sourceIndex = parseInt(e.dataTransfer.getData('text/plain'));
    
    if (sourceIndex === targetIndex) return;
    
    const newSelected = [...selectedValues];
    const [movedItem] = newSelected.splice(sourceIndex, 1);
    newSelected.splice(targetIndex, 0, movedItem);
    
    onChange(newSelected);
    setDraggedIndex(null);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedIndex(null);
  };

  const getOptionLabel = (value) => {
    const option = options.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  return (
    <div className={`multi-select-container ${className}`} ref={dropdownRef}>
      <div className="selected-items">
        {selectedValues.map((value, index) => (
          <button
            key={`${value}-${index}`}
            className={`selected-item ${draggedIndex === index ? 'dragging' : ''}`}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            onClick={(e) => {
              e.preventDefault();
            }}
          >
            <span className="item-index">{index + 1}</span>
            <span className="item-label">{getOptionLabel(value)}</span>
            <button
              type="button"
              className="remove-btn"
              onClick={() => handleRemove(value)}
              aria-label={`Remove ${getOptionLabel(value)}`}
              disabled={disabled}
            >
              ×
            </button>
          </button>
        ))}
      </div>
      {selectedValues.length < maxSelection && (
        <div className="input-container">
          {enableSearch ? (
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setIsOpen(true)}
              placeholder={searchPlaceholder}
              className="multi-select-input"
              disabled={disabled}
            />
          ) : (
            <button 
              className="multi-select-toggle"
              onClick={() => !disabled && setIsOpen(!isOpen)}
            >
              {placeholder}
            </button>
          )}
          
          {isOpen && (
            <div className="options-dropdown">
              {filteredOptions.length === 0 ? (
                <div className="option-item no-options">
                  {noOptionsMessage}
                </div>
              ) : (
                filteredOptions.map(option => (
                  <button
                    key={option.value}
                    className="option-item"
                    onClick={() => handleSelect(option.value)}
                  >
                    {option.label}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {selectedValues.length >= maxSelection && (
        <div className="max-selection-message">
          {maxSelectionMessage}
        </div>
      )}
    </div>
  );
};

MultiSelectInput.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    })
  ),
  selectedValues: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  onChange: PropTypes.func.isRequired,
  maxSelection: PropTypes.number,
  placeholder: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  noOptionsMessage: PropTypes.string,
  maxSelectionMessage: PropTypes.string,
  enableSearch: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string
};

export default MultiSelectInput;
